/* eslint-disable max-lines */
import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isAuthenticated } from '@redux/selectors/auth';
import { apiGetCompanies } from '@redux/action/companies';

const Login = React.lazy(() => import('./pages/auth/Login'));
const Home = React.lazy(() => import('./pages/home'));
const NotFound = React.lazy(() => import('./pages/notfound'));
const Dashboard = React.lazy(() => import('./pages/dashboards'));
const AssetList = React.lazy(() => import('./pages/assetList/AssetList'));
const AssetDetails = React.lazy(() => import('./pages/asset/assetDetails'));
const GeospatialData = React.lazy(() => import('./pages/geospatial'));
const Mapping = React.lazy(() => import('./pages/mapping'));
const DataMining = React.lazy(() => import('./pages/data-mining'));
const GeospatialDataExternalView = React.lazy(() => import('./pages/geospatial/external-view'));

// auth
const RealityMesh = React.lazy(() => import('./pages/realityMesh'));
const PointCloud = React.lazy(() => import('./pages/pointCloud'));
const AddInspection = React.lazy(() => import('./pages/inspection/add'));
const ViewInspection = React.lazy(() => import('./pages/inspection/view'));
const Inspection = React.lazy(() => import('./pages/inspection'));
const Report = React.lazy(() => import('./pages/report/Report'));
const Logout = React.lazy(() => import('./pages/auth/Logout'));
const ForgetPassword = React.lazy(() => import('./pages/account/ForgetPassword'));
const Register = React.lazy(() => import('./pages/account/Register'));
const ConfirmAccount = React.lazy(() => import('./pages/account/Confirm'));
const IFrame = React.lazy(() => import('./pages/iFrame/iFrame'));

// handle auth and authorization
const PrivateRouteWithRedux = ({ component: Component, roles, ...rest }) => {
  const {
    companies,
    auth: { user },
  } = useSelector(s => s);
  const isAuth = useSelector(isAuthenticated);
  React.useEffect(() => {
    if (isAuth && !companies.isLoading && companies.data.length === 0) {
      apiGetCompanies();
    }
  }, [isAuth]);
  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isAuth) {
          // not logged in so redirect to login page with the return url
          return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />;
        }

        // check if route is restricted by role
        if (roles && roles.indexOf(user.role) === -1) {
          // role not authorised so redirect to home page
          return <Redirect to={{ pathname: '/' }} />;
        }

        // authorised so return component
        return <Component {...props} />;
      }}
    />
  );
};

const routes = [
  // does not require token
  {
    path: '/login',
    name: 'Login',
    exact: true,
    component: Login,
    route: Route,
  },
  {
    path: '/logout',
    name: 'Logout',
    exact: true,
    component: Logout,
    route: Route,
  },
  {
    path: '/asset',
    exact: true,
    name: 'AssetList',
    component: AssetList,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/inspection/add',
    name: 'AddInspection',
    exact: true,
    component: AddInspection,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/inspection/view/:inspection_id',
    name: 'ViewInspection',
    exact: true,
    component: ViewInspection,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/inspection',
    name: 'Inspection',
    exact: true,
    component: Inspection,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/report',
    name: 'Report',
    exact: true,
    component: Report,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/reality-mesh',
    name: 'RealityMesh',
    exact: true,
    component: RealityMesh,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/point-cloud',
    name: 'PointCloud',
    exact: true,
    component: PointCloud,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/geospatial-data',
    name: 'GeospatialData',
    exact: true,
    component: GeospatialData,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/asset/:asset_id/mapping',
    name: 'Mapping',
    exact: true,
    component: Mapping,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/forget-password',
    name: 'Forget Password',
    exact: true,
    component: ForgetPassword,
    route: Route,
  },
  {
    path: '/register',
    name: 'Register',
    exact: true,
    component: Register,
    route: Route,
  },
  {
    path: '/confirm',
    name: 'Confirm',
    exact: true,
    component: ConfirmAccount,
    route: Route,
  },
  {
    path: '/home',
    name: 'Home',
    exact: true,
    component: Home,
    route: PrivateRouteWithRedux,
    roles: ['admin', 'client', 'superAdmin'],
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    exact: true,
    component: Dashboard,
    route: PrivateRouteWithRedux,
    roles: ['admin', 'client', 'superAdmin'],
  },
  {
    path: '/asset/:asset_id',
    exact: true,
    name: 'AssetDetails',
    component: AssetDetails,
    route: PrivateRouteWithRedux,
    roles: ['admin', 'client', 'superAdmin'],
  },
  {
    path: '/data-mining/:asset_id?',
    exact: true,
    name: 'DataMining',
    component: DataMining,
    route: PrivateRouteWithRedux,
    roles: ['admin', 'client', 'superAdmin'],
  },
  {
    path: '/',
    exact: true,
    component: () => <Redirect to="/home" />,
    route: PrivateRouteWithRedux,
  },
  {
    path: '/external/view-map/:asset_id',
    name: 'ViewMap',
    isExternal: true,
    exact: true,
    component: GeospatialDataExternalView,
    route: Route,
  },
  {
    path: '/show/:asset_id/:type',
    exact: true,
    hideSideBar: true,
    name: 'IFrame',
    component: IFrame,
    route: PrivateRouteWithRedux,
  },
  {
    component: () => <NotFound />,
    route: Route,
  },
];

export { routes };
